import React from 'react';
import { Box, Container, Button, useTheme, useMediaQuery } from '@mui/material';
import { useWebsiteSettings } from '../../contexts/WebsiteSettingsContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppMode } from '../../contexts/AppModeContext';
import MegaMenu, { defaultMegaMenuData } from './MegaMenu';

const SecondaryNavBar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTouch = useMediaQuery('(hover: none) and (pointer: coarse)');
  const navigate = useNavigate();
  const location = useLocation();
  const { is_job } = useAppMode();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [hoveredCategory, setHoveredCategory] = React.useState<string | null>(null);
  const closeTimer = React.useRef<number | null>(null);
  const openTimer = React.useRef<number | null>(null);
  const [hidden, setHidden] = React.useState(false);
  const barRef = React.useRef<HTMLDivElement | null>(null);
  const [anchorTop, setAnchorTop] = React.useState<number>(0);
  const lastY = React.useRef<number>(typeof window !== 'undefined' ? window.scrollY : 0);
  const lastDir = React.useRef<'up' | 'down' | null>(null);
  const [categoriesEnabled, setCategoriesEnabled] = React.useState<Record<string, boolean>>({});
  // Global layout settings (container width and horizontal padding)
  const { settings: siteSettings } = useWebsiteSettings();
  const layout = (siteSettings?.layout || {}) as any;
  const maxWidth = typeof layout.container === 'string' && layout.container !== 'false' ? (layout.container as any) : false;
  const px = typeof layout.contentPaddingX === 'number' ? layout.contentPaddingX : 0;

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const clearOpenTimer = () => {
    if (openTimer.current) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  };

  React.useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - lastY.current;
      const direction: 'up' | 'down' = delta > 2 ? 'down' : delta < -2 ? 'up' : (lastDir.current || 'down');
      if (direction !== lastDir.current) {
        lastDir.current = direction;
        setHidden(direction === 'down' && y > 10);
      } else if (y < 10) {
        setHidden(false);
      }
      lastY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Keep anchorTop in sync with layout
  React.useEffect(() => {
    const compute = () => {
      const headerHeight = isMobile ? 44 : 52;
      const headerBorder = 1;
      if (hidden) {
        setAnchorTop(headerHeight + headerBorder);
        return;
      }
      const el = barRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        // rect accounts for transform; bottom is the visual bottom in viewport
        setAnchorTop(Math.max(headerHeight, Math.floor(rect.bottom)));
      } else {
        setAnchorTop(headerHeight + 40);
      }
    };
    compute();
    const ro = new ResizeObserver(() => compute());
    if (barRef.current) ro.observe(barRef.current);
    window.addEventListener('scroll', compute, { passive: true });
    window.addEventListener('resize', compute);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', compute as any);
      window.removeEventListener('resize', compute);
    };
  }, [hidden, isMobile, menuOpen]);

  // Close mega menu when bar hides
  React.useEffect(() => {
    if (hidden) setMenuOpen(false);
  }, [hidden]);

  // Load website settings to determine which categories are enabled
  React.useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetch('/website-settings.json', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        const existing: Record<string, boolean> = data?.categoriesEnabled || {};
        setCategoriesEnabled(existing);
      } catch (e) {
        console.error('Failed to load website settings', e);
      }
    };
    loadSettings();
  }, []);

  // Only show in shop mode and only on desktop/tablet (hide for touch devices too)
  if (is_job) return null;
  if (isMobile || isTouch) return null;

  const links: Array<{ label: string; path: string }> = [
    { label: 'Damen', path: '/all-products?category=Damen' },
    { label: 'Herren', path: '/all-products?category=Herren' },
    { label: 'Designerartikel', path: '/all-products?category=Designer' },
    { label: 'Kinder', path: '/all-products?category=Kinder' },
    { label: 'Home', path: '/all-products?category=Home' },
    { label: 'Elektronik', path: '/all-products?category=Elektronik' },
    { label: 'Unterhaltung', path: '/all-products?category=Unterhaltung' },
    { label: 'Hobby- & Sammlerartikel', path: '/all-products?category=Hobby-%20%26%20Sammlerartikel' },
    { label: 'Traditionell', path: '/all-products?category=Traditionell' },
    { label: 'Sport', path: '/all-products?category=Sport' },
    { label: 'Unsere Plattform', path: '/about' },
  ];

  return (
    <Box
      ref={barRef}
      sx={{
        position: 'fixed',
        top: { xs: 44, md: 52 },
        left: 0,
        right: 'auto',
        width: 'calc(100% - var(--right-panel-offset, 0px))',
        height: 40,
        bgcolor: 'background.paper',
        borderBottom: menuOpen ? 'none' : '1px solid',
        borderColor: 'divider',
        zIndex: (theme) => theme.zIndex.appBar - 1,
        display: 'flex',
        alignItems: 'center',
        transform: hidden ? 'translateY(-100%)' : 'translateY(0)',
        transition: 'transform 180ms ease',
        boxShadow: hidden || menuOpen ? 'none' : '0 2px 6px rgba(0,0,0,0.06)',
      }}
      onMouseLeave={() => {
        if (isMobile) return;
        clearOpenTimer();
        clearCloseTimer();
        closeTimer.current = window.setTimeout(() => setMenuOpen(false), 160) as unknown as number;
      }}
      onMouseEnter={() => {
        if (isMobile) return;
        clearCloseTimer();
      }}
    
    >
      <Container maxWidth={maxWidth} sx={{ px }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            '::-webkit-scrollbar': { height: 6 },
            '::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: 999 },
          }}
        >
          {links
            .filter((link) => {
              // If the link label exists in categoriesEnabled and is false, hide it.
              // If not present in the map, keep it visible (e.g., non-category links).
              if (Object.prototype.hasOwnProperty.call(categoriesEnabled, link.label)) {
                return !!categoriesEnabled[link.label];
              }
              return true;
            })
            .map((link) => {
            const isActive = location.pathname + location.search === link.path;
            return (
              <Button
                key={link.label}
                size={isMobile ? 'small' : 'medium'}
                onClick={() => {
                  clearOpenTimer();
                  clearCloseTimer();
                  setHoveredCategory(link.label);
                  setMenuOpen(true);
                }}
                onMouseEnter={() => {
                  if (isMobile) return;
                  clearCloseTimer();
                  setHoveredCategory(link.label);
                  if (!menuOpen) {
                    clearOpenTimer();
                    openTimer.current = window.setTimeout(() => setMenuOpen(true), 1000) as unknown as number;
                  } else {
                    setMenuOpen(true);
                  }
                }}
                onMouseLeave={() => {
                  if (isMobile) return;
                  clearOpenTimer();
                }}
                sx={{
                  textTransform: 'none',
                  color: isActive ? 'primary.main' : 'text.secondary',
                  fontWeight: isActive ? 700 : 500,
                  fontSize: { xs: '0.85rem', md: '0.95rem' },
                  px: 1.25,
                  minWidth: 'auto',
                  '&:hover': { color: 'primary.main', background: 'transparent' },
                }}
              >
                {link.label}
              </Button>
            );
          })}
        </Box>
      </Container>
      <MegaMenu
        open={menuOpen}
        category={hoveredCategory}
        data={defaultMegaMenuData}
        onClose={() => {
          clearCloseTimer();
          setMenuOpen(false);
        }}
        onEnter={clearCloseTimer}
        secondaryHidden={hidden}
        anchorTop={anchorTop}
      />
    </Box>
  );
};

export default SecondaryNavBar;
